/**
 * Resolving the layer masks against a real skeleton.
 *
 * `BONE_MASKS` in `@dungeon-builder/shared` names bones; a loaded skeleton has them.
 * This is where the two meet, and where a mismatch surfaces as data rather than as a
 * character whose left leg silently stops animating.
 *
 * First brick of the layering system (M2). The groups this produces are what
 * `animationLayerRig.ts` filters `AnimationGroup`s down to in step 2.2.
 */
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import { BONE_MASKS } from '@dungeon-builder/shared';

export interface ResolvedBoneMasks {
  /** Bone names present on the skeleton and claimed by the lower layer. */
  lower: string[];
  /** Bone names present on the skeleton and claimed by the upper layer. */
  upper: string[];
  /**
   * Bones the skeleton has that neither mask claims.
   *
   * `Root` belongs here and is expected -- the battle server owns position, so
   * nothing animates it. Anything else here is a bone no layer drives, which is a
   * bug in the masks rather than in the skeleton. Step 2.1's gate reads this.
   */
  unclaimed: string[];
  /** Bones the masks name that the skeleton does not have. */
  missing: string[];
}

export function resolveBoneMasks(skeleton: Skeleton): ResolvedBoneMasks {
  const present = new Set(skeleton.bones.map((bone) => bone.name));
  const claimed = new Set<string>();

  const take = (names: readonly string[]): string[] => {
    const found: string[] = [];
    for (const name of names) {
      if (!present.has(name)) continue;
      found.push(name);
      claimed.add(name);
    }
    return found;
  };

  const lower = take(BONE_MASKS.lower);
  const upper = take(BONE_MASKS.upper);

  const named = new Set([...BONE_MASKS.lower, ...BONE_MASKS.upper]);
  return {
    lower,
    upper,
    unclaimed: [...present].filter((name) => !claimed.has(name)),
    missing: [...named].filter((name) => !present.has(name)),
  };
}
